import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { checksAPI, banksAPI } from '../services/api';

const checkTypeOptions = [
  { value: 'cheque', label: 'Chèque' },
  { value: 'effect', label: 'Effet' },
  { value: 'transfer', label: 'Virement' },
  { value: 'other', label: 'Autre' },
];

const directionOptions = [
  { value: 'incoming', label: 'Entrant' },
  { value: 'outgoing', label: 'Sortant' },
];

const statusOptions = [
  { value: 'draft', label: 'Brouillon' },
  { value: 'pending', label: 'En attente' },
  { value: 'cleared', label: 'Encaissé' },
  { value: 'bounced', label: 'Impayé' },
  { value: 'cancelled', label: 'Annulé' },
];

function CheckFormModal({ open, onClose, check }) {
  const isEditing = !!check;
  const [banks, setBanks] = useState([]);
  const [formData, setFormData] = useState({
    check_number: '',
    check_type: 'cheque',
    direction: 'incoming',
    bank: '',
    bank_name: '',
    amount: '',
    currency: 'DZD',
    issue_date: '',
    due_date: '',
    beneficiary: '',
    status: 'pending',
    notes: '',
  });
  const [proofImage, setProofImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      loadBanks();
    }
  }, [open]);

  const loadBanks = async () => {
    try {
      const response = await banksAPI.getAll();
      setBanks(response.data || []);
    } catch (err) {
      console.error('Error loading banks:', err);
    }
  };

  useEffect(() => {
    if (check) {
      setFormData({
        check_number: check.check_number || '',
        check_type: check.check_type || 'cheque',
        direction: check.direction || 'incoming',
        bank: check.bank || '',
        bank_name: check.bank_name || '',
        amount: check.amount || '',
        currency: check.currency || 'DZD',
        issue_date: check.issue_date || '',
        due_date: check.due_date || '',
        beneficiary: check.beneficiary || '',
        status: check.status || 'pending',
        notes: check.notes || '',
      });
    } else {
      setFormData({
        check_number: '',
        check_type: 'cheque',
        direction: 'incoming',
        bank: '',
        bank_name: '',
        amount: '',
        currency: 'DZD',
        issue_date: '',
        due_date: '',
        beneficiary: '',
        status: 'pending',
        notes: '',
      });
    }
    setProofImage(null);
    setError(null);
  }, [check, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProofImage(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          payload.append(key, value);
        }
      });
      if (proofImage) {
        payload.append('proof_image', proofImage);
      }

      if (isEditing) {
        await checksAPI.update(check.id, payload);
      } else {
        await checksAPI.create(payload);
      }
      onClose(true);
    } catch (err) {
      console.error('Error saving check:', err);
      const msg = err.response?.data?.detail ||
        err.response?.data?.proof_image?.[0] ||
        err.response?.data?.check_number?.[0] ||
        'Échec de l\'enregistrement du chèque. Veuillez vérifier les données.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {isEditing ? 'Modifier le chèque' : 'Nouveau chèque'}
          </Typography>
          <IconButton onClick={() => onClose(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box component="form" id="check-form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="check_number"
                label="N° de chèque *"
                value={formData.check_number}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type de chèque</InputLabel>
                <Select
                  name="check_type"
                  value={formData.check_type}
                  label="Type de chèque"
                  onChange={handleChange}
                >
                  {checkTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Direction</InputLabel>
                <Select
                  name="direction"
                  value={formData.direction}
                  label="Direction"
                  onChange={handleChange}
                >
                  {directionOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Banque *</InputLabel>
                <Select
                  name="bank"
                  value={formData.bank}
                  label="Banque *"
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="">-- Sélectionner une banque --</MenuItem>
                  {banks.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name} ({b.current_balance?.toLocaleString()} {b.currency})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="amount"
                label="Montant *"
                type="number"
                value={formData.amount}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="currency"
                label="Devise"
                value={formData.currency}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="issue_date"
                label="Date d\'émission *"
                type="date"
                value={formData.issue_date}
                onChange={handleChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="due_date"
                label="Date d\'échéance"
                type="date"
                value={formData.due_date}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="beneficiary"
                label="Bénéficiaire"
                value={formData.beneficiary}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  label="Statut"
                  onChange={handleChange}
                >
                  {statusOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes"
                value={formData.notes}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth sx={{ py: 1 }}>
                {proofImage ? `Sélectionné: ${proofImage.name}` : 'Téléverser la preuve du chèque *'}
                <input
                  type="file"
                  accept="image/*,.pdf,application/pdf"
                  hidden
                  onChange={handleImageChange}
                />
              </Button>
              {isEditing && check?.proof_image_url && (
                <Box sx={{ mt: 1 }}>
                  {check.proof_image_url.toLowerCase().endsWith('.pdf') ? (
                    <Button
                      variant="outlined"
                      size="small"
                      href={check.proof_image_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Voir le PDF
                    </Button>
                  ) : (
                    <img
                      src={check.proof_image_url}
                      alt="Preuve du chèque"
                      style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4, border: '1px solid #ddd' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => onClose(false)} disabled={loading}>
          Annuler
        </Button>
        <Button
          type="submit"
          form="check-form"
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Enregistrement...' : isEditing ? 'Enregistrer les modifications' : 'Créer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CheckFormModal;
